import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import React, { Suspense, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StyledPricing from './styles/PricingPageStyles';
import Dither from '@/components/landing/pricing/BigDither';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import PricingCards from '@/components/landing/pricing/PricingCard';

const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadFAQ = () => import('@/components/landing/pricing/FAQ');
const FAQ = React.lazy(loadFAQ);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);

const COLORS = [
  [0.6938493924808276, 0.2689473853651021, 0.831974417082523],
  [0.1494656501053092, 0.4319631286758373, 0.22207953805581548],
  [0.23663014459162568, 0.9880407369034765, 0.7556320432549344],
  [0.7643497404669855, 0.2730390035335093, 0.6660689449814485],
  [0.015396646783305323, 0.7103582708008104, 0.1266671933772835],
  [0.019712849744326078, 0.7108717662256677, 0.1472532950398595],
  [0.0772671355972605, 0.7499278200719359, 0.9832381235866448],
  [0.5471492100429514, 0.9762876959978107, 0.11446098259059712],
  [0.10657557366963155, 0.7987767419267344, 0.5108610490933111],
  [0.9803802558532599, 0.20355759246085603, 0.822573236433734],
  [0.9803802558532599, 0.20355759246085603, 0.822573236433734],
  [0.5352619158288408, 0.06615431342688238, 0.6722058785034406],
  [0.06468596226894119, 0.6098920888145747, 0.5149604192746544],
  [0.8569712922130701, 0.6109576265522961, 0.08676107376344588],
  [0.27264173724127094, 0.9055163774390819, 0.6977057682372749],
  [0.9890173447398914, 0.2027746619393863, 0.9709624937241823]
]

const PricingPage: React.FC = () => {
  useIdlePrefetch([loadPricingTable, loadFAQ, loadGetStarted, loadFooter]);
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [waveColors, setWaveColors] = useState([])

  useEffect(() => {
    window.billingMountedIntentExecuted = false
    window.track('pricing_page_visited')
  }, []);


  useEffect(() => {
    setWaveColors(COLORS[Math.floor(Math.random() * COLORS.length)])
  }, [])

   return (
    <>
      <SEOMetadata
        title="Pricing – Financial Data API | AxionQuant"
        description="Simple, transparent pricing for the AxionQuant financial data API. Free tier available. Paid plans from $29/month."
        canonicalUrl="https://axionquant.com/pricing"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "AxionQuant Financial Data API Plans",
          "description": "Transparent financial data API and stock market data pricing. From individual traders to global institutions, start free and scale as you grow.",
          "brand": {
            "@type": "Brand",
            "name": "AxionQuant"
          },
          "image": "https://axionquant.com/meta.jpg",
          "offers": [
            { "@type": "Offer", "name": "Free", "price": "0", "priceCurrency": "USD", "availability": "https://schema.org/InStock" },
            { "@type": "Offer", "name": "Basic", "price": "29", "priceCurrency": "USD", "availability": "https://schema.org/InStock" },
            { "@type": "Offer", "name": "Pro", "price": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock" }
          ]
        })}</script>
      </Helmet>

      <StyledPricing>
        <div className="hero">
          <div className="background">
          { waveColors.length > 0 &&
              <Dither
                waveColor={waveColors}
                disableAnimation={false}
                enableMouseInteraction={true}
                mouseRadius={0.3}
                colorNum={4}
                waveAmplitude={0.3}
                waveFrequency={3}
                waveSpeed={0.05}
              />
          }
          </div>

          <div className="content">
            <h1 className="serif">
              Financial Data API Pricing <br/> for Every Stage of Research
            </h1>
            <p>
              From individual traders to global institutions, we have a financial data API plan that fits your needs.
              Start free, scale as you grow.
            </p>
            </div>

        </div>

        <div className="billing-toggle">
          <button
            className={billingPeriod === 'monthly' ? 'active' : ''}
            onClick={() => setBillingPeriod('monthly')}
          >
            Monthly
          </button>
          <button
            className={billingPeriod === 'annual' ? 'active' : ''}
            onClick={() => setBillingPeriod('annual')}
          >
            Annual
            <span className="badge">Save up to 20%</span>
          </button>
        </div>

        <PricingCards billingPeriod={billingPeriod} />
        <Suspense fallback={null}>

        <div className="comparison">
          <PricingTable
            title={'Compare All Features'}
            subtitle={'Everything you need to know about our plans'}
          />
        </div>

        <FAQ/>

        <GetStarted
          title="Ready to Get Started?"
          text="Join thousands of traders, analysts, and institutions using the AxionQuant financial data API to make better research decisions with daily and historical data and advanced analytics."
          primary={{ title: "Start for free", url: "/login" }}
          secondary={{ title: "Talk to sales", url: "/contact" }}
        />

        <Footer />
        </Suspense>
      </StyledPricing>
    </>
  );
};

export default PricingPage;

